import React, { useState } from 'react'
import { Controller } from 'react-hook-form'
import { Upload, X, FileText } from 'lucide-react'
import { EyeIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { useObjectUrl } from 'hooks/useObjectUrl'

const MAX_FILE_SIZE_MB = 5
const PDF_MIME_TYPE = 'application/pdf'

interface PdfUploadProps {
    label?: string
    name: string
    control: any
    errors?: any
    rules?: any
    required?: boolean
    tooltip?: {
        title?: string
        items: string[]
        position?: 'top' | 'bottom' | 'left' | 'right'
    }
    fileSizeLimit?: {
        min_size: number
        max_size: number
        message: string
    }
}

const PdfViewerFrame = ({ value }: { value: File | string | null }) => {
    const pdfUrl = useObjectUrl(value)

    return (
        <iframe
            src={pdfUrl ?? ''}
            className="w-full h-full rounded-b-lg"
            title="PDF Viewer"
        />
    )
}

const PdfUploadField: React.FC<PdfUploadProps> = ({
    label,
    name,
    control,
    rules,
    required,
    tooltip,
    fileSizeLimit,
}) => {
    const [localError, setLocalError] = useState<string | null>(null)
    const [showViewer, setShowViewer] = useState(false)

    const tooltipPositionClass = {
        top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
        left: 'right-full top-1/2 -translate-y-1/2 mr-2',
        right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...rules,
                validate: {
                    fileType: (value: File | string | null) => {
                        if (!value || typeof value === 'string') return true
                        return (
                            value.type === PDF_MIME_TYPE ||
                            'Only PDF files are allowed'
                        )
                    },
                    fileSize: (value: File | string | null) => {
                        if (!value || typeof value === 'string') return true

                        if (fileSizeLimit) {
                            const minBytes =
                                fileSizeLimit.min_size * 1024
                            const maxBytes =
                                fileSizeLimit.max_size * 1024

                            return (
                                (value.size >= minBytes &&
                                    value.size <= maxBytes) ||
                                fileSizeLimit.message
                            )
                        }

                        return (
                            value.size / (1024 * 1024) <= MAX_FILE_SIZE_MB ||
                            `File size must be ≤ ${MAX_FILE_SIZE_MB}MB`
                        )
                    },
                },
            }}
            render={({ field, fieldState }) => (
                <div>
                    {/* Label */}
                    <div className="flex items-center gap-1 mb-2">
                        <label
                            className={`text-xs font-medium ${fieldState.error
                                ? 'text-red-600'
                                : 'text-gray-700 dark:text-gray-300'
                                }`}
                        >
                            {label}
                        </label>
                        {required && <span className="text-red-700">*</span>}
                        {tooltip && (
                            <div className="relative group">
                                <InformationCircleIcon className="w-4 h-4 text-slate-400 cursor-pointer" />
                                <div
                                    className={`absolute ${tooltipPositionClass[
                                        tooltip.position ?? 'top'
                                    ]
                                        } w-64 rounded-md z-50 bg-white border p-3 text-xs shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible dark:bg-slate-700 dark:border-gray-600 dark:text-gray-300 transition-opacity`}
                                >
                                    {tooltip.title && (
                                        <p className="font-semibold mb-1">
                                            {tooltip.title}
                                        </p>
                                    )}
                                    <ul className="space-y-1">
                                        {tooltip.items.map((item, idx) => (
                                            <li key={idx}>• {item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Upload Box */}
                    <label
                        htmlFor={name}
                        className="flex justify-center items-center gap-4 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:border-blue-400 transition"
                    >
                        {field.value ? (
                            <div className="flex items-center gap-3 relative w-full">
                                <FileText
                                    size={28}
                                    className="text-red-500"
                                />

                                <div className="flex-1 text-sm truncate dark:text-gray-200">
                                    {typeof field.value === 'string'
                                        ? field.value.split('/').pop()
                                        : field.value.name}
                                </div>

                                {/* View PDF */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setShowViewer(true)
                                    }}
                                    className="text-blue-600 text-xs underline mr-6"
                                >
                                    {/* View */}
                                    <EyeIcon className='h-4 w-4 text-blue-800 dark:text-blue-500' />
                                </button>

                                {/* Remove */}
                                <button
                                    type="button"
                                    className="absolute right-0 bg-white rounded-full p-1 shadow"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        field.onChange(null)
                                        const input =
                                            document.getElementById(
                                                name
                                            ) as HTMLInputElement
                                        if (input) input.value = ''
                                    }}
                                >
                                    <X
                                        size={14}
                                        className="text-red-500"
                                    />
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                <Upload
                                    className="text-gray-400 mb-2"
                                    size={20}
                                />
                                <p className="text-sm text-gray-500">
                                    Click to upload PDF
                                </p>
                            </div>
                        )}
                    </label>

                    {/* Hidden Input */}
                    <input
                        id={name}
                        type="file"
                        className="hidden"
                        accept="application/pdf"
                        onChange={(e) => {
                            setLocalError(null)
                            const file = e.target.files?.[0] ?? null
                            if (!file) return

                            // 🔐 Hard validation (handles "ALL files")
                            if (file.type !== PDF_MIME_TYPE) {
                                setLocalError(
                                    'Only PDF files are allowed'
                                )
                                e.target.value = ''
                                field.onChange(null)
                                return
                            }

                            field.onChange(file)
                        }}
                    />

                    {/* Error */}
                    {(fieldState.error || localError) && (
                        <p className="text-red-500 text-xs mt-1">
                            {fieldState.error?.message || localError}
                        </p>
                    )}

                    {/* PDF Viewer Modal */}
                    {showViewer && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                            <div
                                className="bg-white w-[90%] h-[90%] rounded-lg shadow-lg relative overflow-hidden"
                            >
                                <button
                                    //    w-[30px] h-[30px]
                                    // absolute top-[-5px] right-[-5px]
                                    className="
                               absolute top-[-8px] right-[-8px]
                               z-[60]
                               w-[36px] h-[36px]
                               flex items-center justify-center
                               rounded-full
                              rgb(57 60 59)
                              text-[#fafafa]
                              shadow-xl
                              transition
                              text-[10px]
                              p-2"
                                    onClick={() => setShowViewer(false)}
                                >
                                    <X size={24} strokeWidth={2.5} />
                                </button>

                                <PdfViewerFrame value={field.value} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        />
    )
}

export default PdfUploadField
